/** @jsx jsx */
import { Button, Grid, Icon, Spacer } from "@cd2/components"
import { H3 } from "@cd2/text"
import { css, jsx } from "@emotion/core"
import React, { useEffect } from "react"
import { DraggableList } from "../../Components/DraggableList"
import { Factory } from "../../Components/Factory"
import { icons } from "../../constants/icons"
import { useConfirmation } from "../../hooks/useConfirmation"
import { calculatePriceFromMargin, generateTempId, isTempId } from "../../utils/utils"
import { CustomProductInput } from "./ItemRowCustomProduct"
import { CustomTextInput } from "./ItemRowCustomText"
import { HeadingRow } from "./ItemRowHeading"
import { SageProductInput } from "./ItemRowSageProduct"
import { TotalsRow } from "./ItemRowTotal"

import { Fields } from "@cd2/forms/build/es6"
import { useBetaFeatureAccess } from "../../Components/BetaFeatureAccess"
import { ButtonsWrapper } from "../../Components/ButtonsWrapper"
import { brandColors } from "../../Components/EOSThemeProvider"
import { useGlobalState } from "../../Components/GlobalStateProvider"
import { IKitBuilderRow, IKitFile, useQuoteKitBuilder } from "../../hooks/useQuoteKitBuilder"
import { useSiteAlerts } from "../../hooks/useSiteAlerts"
import { useBuildSetBuilder } from "../../Pages/BuildSets/useBuildSetBuilder"
import { useSettings } from "../../utils/useSettings"
import { ItemRowBuildSetProduct } from "./ItemRowBuildSetProduct"
import {
  blankNewItem,
  filterDeleted,
  genItemParentId,
  getProductItemChildren,
  IDeletedItem,
  IItem,
  IQuoteItemRegisterOptions,
  IQuoteOrderRecord,
  IRowProps,
  isDeleted,
  itemIsChild,
  itemIsParent,
  itemTypes,
  parentOfItem,
} from "./quoteOrderUtils"
import { QuoteTotals } from "./QuoteTotals"
import { RowContainer } from "./RowContainer"
import { SageLogo } from "./SageLogo"

export const rowComponents: Record<itemTypes, (props: IRowProps) => JSX.Element> = {
  "0": SageProductInput,
  "1": CustomTextInput,
  "2": CustomProductInput,
  "3": SageProductInput,
  "4": ItemRowBuildSetProduct,
}

interface IItemTableProps {
  type: "quote" | "order"
  quote: IQuoteOrderRecord
  setQuote: React.Dispatch<React.SetStateAction<IQuoteOrderRecord>>
  // set data without triggering dirty state
  setQuoteSilent: React.Dispatch<React.SetStateAction<IQuoteOrderRecord>>
  errors: any
  registeredItems: Record<string, IQuoteItemRegisterOptions>
  isNew?: boolean

  registerItem(item: IItem, options: IQuoteItemRegisterOptions): () => void

  doesItemHaveErrors(item: IItem, opts?: { skipAdditionalChecks?: boolean }): boolean

  isCustomerViewing?: boolean
  hideMarginColumn?: boolean
  hidePriceColumn?: boolean
}

export function ItemTable({
  type,
  quote,
  setQuote,
  setQuoteSilent,
  errors,
  registerItem,
  doesItemHaveErrors,
  registeredItems,
  isCustomerViewing,
  hideMarginColumn,
  hidePriceColumn,
}: IItemTableProps) {
  const { currentUser } = useGlobalState()

  // Detect platform for keyboard shortcuts
  const isMac =
    typeof navigator !== "undefined" && navigator.platform.toUpperCase().indexOf("MAC") >= 0
  const modKey = isMac ? "⌘" : "Ctrl"

  function handleAddRowForKitBuilder(item: IKitBuilderRow) {
    const row = handleAddRow(item.item.type ?? itemTypes.sage, {
      ...item.item,
      is_rich_quote_optional: item.optional,
    })
    if (row) {
      item.callback(row.id)
    }
  }

  function handleAddFiles(files: IKitFile[]) {
    setQuote(quote => {
      const prevFiles = quote.files || []
      return { ...quote, files: [...prevFiles, ...files.map(x => ({ ...x, id: undefined }))] }
    })
  }

  const { modals: kitBuilderModal, handleBuildQuoteKit } = useQuoteKitBuilder(quote, {
    onAddItem: handleAddRowForKitBuilder,
    onAddFiles: handleAddFiles,
  })

  const {
    modals: buildSetBuilderModal,
    handleCreateBuildSet,
    loadProductDataForSageItemRow,
  } = useBuildSetBuilder(quote, {
    onAddPrimary(item, uid) {
      const row = handleAddRow(itemTypes.buildSet, {
        ...item.item,
        is_rich_quote_optional: item.optional,
        build_set_uid: uid,
      })
      if (row) {
        item.callback(row.id)
      }
      console.log("Appended Primary", row)
      return row
    },
    onAddSubItem(item, uid) {
      const row = handleAddRow(itemTypes.buildSetItem, {
        ...item.item,
        price_pennies: null,
        markUp: null,
        build_quantity: item.item.quantity,
        quantity: item.item.quantity,
        is_rich_quote_optional: item.optional,
        build_set_uid: uid,
      })
      if (row) {
        item.callback(row.id)
      }
      console.log("Appended Sub", row)
    },
    onComplete(_item, _data, _uid) {},
  })

  async function handleAddKit() {
    if (!currentUser.branch.id) {
      return
    }
    const finalData = await handleBuildQuoteKit(currentUser.branch.id)
    if (!finalData) {
      return
    }
    alerts.genericSuccess("Set added to quote")
  }

  async function handleAddBuildSet() {
    if (!currentUser.branch.id) {
      return
    }
    const finalData = await handleCreateBuildSet(currentUser.branch.id, quote.customer?.id)
    if (!finalData) {
      return
    }
    alerts.genericSuccess("Set added to quote")
  }

  const alerts = useSiteAlerts()
  const settings = useSettings()
  const betaFeature = useBetaFeatureAccess()

  const [autoAllocateMax, setAutoAllocateMax] = React.useState(true)
  const [dynamicModal, renderDynamicModal] = useConfirmation<
    { Component: (item: { onResolve: (data: any) => any }) => any },
    any
  >(({ data: { Component }, onResolve }) =>
    Component ? <Component onResolve={onResolve} /> : null,
  )

  useEffect(() => {
    if (autoAllocateMax) {
      setQuote({
        ...quote,
        items: quote.items.map(item =>
          isDeleted(item) ? item : { ...item, allocated_quantity: item.quantity },
        ),
      })
    }
  }, [autoAllocateMax])
  ;(window as any).itemTable = { handleAddRow }

  function handleAddRow(itemType: itemTypes, initialData?: Partial<IItem>) {
    // if (quote.items.length >= 1000) {
    //   alerts.genericError("You can only have a maximum of 100 items in a quote or order.")
    //   return
    // }
    const defaultValues = { ...blankNewItem, ...(initialData ?? {}) }
    const newItem: (typeof quote.items)[number] = {
      ...defaultValues,
      id: generateTempId(),
      type: itemType,
      fixed_discount_rate: initialData?.fixed_discount_rate ?? null,
      parent_uid: defaultValues.parent_uid || genItemParentId("parent"),
      build_set_uid: initialData?.build_set_uid ?? "",
      build_quantity: initialData?.quantity ?? 1,
      opportunity_certificate_cost_discount_amount_pennies: null,
    }
    setQuote(quote => ({ ...quote, items: [...quote.items, newItem] }))
    return newItem
  }

  // We only reorder non-deleted items, when a reorder happens we must re-insert the deleted items
  function handleItemOrderChange(items: IItem[]) {
    const deletedItems = quote.items.filter(isDeleted)
    const itemIds = items.map(i => i.id)
    const filteredItems = quote.items.filter(item => !itemIds.includes(item.id))
    const sortedItems = sortItemsWithBuilds([...items, ...filteredItems])

    setQuote({ ...quote, items: [...sortedItems, ...deletedItems] })
  }

  function clearWindowCustomerPrices(key: string) {
    ;(window as any).customerPrices ||= {}
    delete (window as any).customerPrices[key]
  }

  /**
   * Remove an item from the quote.
   * If the item has a temp id then just delete it from the array, otherwise, set the $deleted attr
   * @param item
   */
  function handleRemoveItem(item: IItem, opts?: { skipRemoveChildren?: boolean }) {
    clearWindowCustomerPrices(item.id)
    setQuote(quote => {
      let newItems = [...quote.items]
      const itemToRemoveId = item.id
      if (isTempId(itemToRemoveId)) {
        newItems = newItems.filter(item => item.id !== itemToRemoveId)
      } else {
        newItems = newItems.map(item =>
          item.id === itemToRemoveId ? { id: item.id, $deleted: true } : item,
        )
      }
      // Handle childItem removal
      // If item is a "parent" product item, remove its child items
      if (itemIsParent(item) && !opts?.skipRemoveChildren) {
        const childItems = getProductItemChildren({ items: newItems }, item)
        newItems = newItems
          .map(newItem => {
            const matched = childItems.find(c => c.id === newItem.id)
            if (matched) {
              if (isTempId(matched.id)) {
                return null
              } else {
                return { id: matched.id, $deleted: true } as IDeletedItem
              }
            }
            return newItem
          })
          .filter(Boolean)
      }

      return { ...quote, items: newItems }
    })
  }

  function handleItemChange(itemId: string, newItem: IItem | ((oldItem: IItem) => IItem)) {
    setQuote(quote => {
      const newItems = quote.items.map(item => {
        if ("$deleted" in item) {
          return item
        } else if (item.id === itemId) {
          return typeof newItem === "function" ? newItem(item) : newItem
        } else {
          return item
        }
      })
      return { ...quote, items: newItems }
    })
  }

  function handleItemChangeSilent(itemId: string, newItem: IItem | ((oldItem: IItem) => IItem)) {
    setQuoteSilent(quote => {
      const newItems = quote.items.map(item => {
        if ("$deleted" in item) {
          return item
        } else if (item.id === itemId) {
          return typeof newItem === "function" ? newItem(item) : newItem
        } else {
          return item
        }
      })
      return { ...quote, items: newItems }
    })
  }

  /**  Update items in a single state change, used for updating childItems*/
  function batchItemChange(items: IItem[], newItem: (item: IItem) => IItem) {
    const itemIds = items.map(v => v.id)
    setQuote(quote => {
      const newItems = quote.items.map(item => {
        if ("$deleted" in item) {
          return item
        } else if (itemIds.includes(item.id)) {
          return newItem(item)
        } else {
          return item
        }
      })
      return { ...quote, items: newItems }
    })
  }

  // convert to ref - resolves issue where state is not up to date in hooks
  const errorsRef = React.useRef(errors)
  errorsRef.current = errors

  function getErrorsForItem(item: IItem) {
    return errorsRef.current.items?.[item.id] || {}
  }

  /** Filter out child items.*/
  function filterParentProductSageItems(items: IItem[]) {
    const final: IItem[] = []
    for (const item of items) {
      // If the item is not a sage product, or the item is not a child, or the item is a child but it has no parent
      if (
        item.type !== itemTypes.sage ||
        !itemIsChild(item) ||
        (!parentOfItem(item, items) && !parentOfItem(item, final))
      ) {
        // If item is a child, make it a parent item instead, but retain its old uid so that other
        // items that were nested can be parented to it
        const parentId = itemIsChild(item)
          ? item.parent_uid.replace("C", "P")
          : !item.parent_uid
          ? genItemParentId("parent", item.id)
          : item.parent_uid
        final.push({ ...item, parent_uid: parentId })
      }
    }
    return final
  }

  const filteredItems = filterParentProductSageItems(filterDeleted(quote.items))

  // Add keyboard shortcut handlers
  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      const cmdOrCtrl = isMac ? e.metaKey : e.ctrlKey

      if (cmdOrCtrl && e.key.toLowerCase() === "s") {
        e.preventDefault()
        handleAddRow(itemTypes.sage)
      } else if (cmdOrCtrl && e.key.toLowerCase() === "d" && type === "quote") {
        e.preventDefault()
        handleAddRow(itemTypes.custom)
      } else if (cmdOrCtrl && e.key.toLowerCase() === "g") {
        e.preventDefault()
        handleAddRow(itemTypes.text)
      }
    }

    window.addEventListener("keydown", handleKeyDown)
    return () => window.removeEventListener("keydown", handleKeyDown)
  }, [isMac, type])

  function handleItemDataOverride(data?: {
    margin?: number | null
    discountPercent?: number | null
  }) {
    if (data && typeof data.margin === "number") {
      const newMargin = data.margin

      batchItemChange(filteredItems, item => {
        if (item.cost_price_pennies && !item.price_locked) {
          return {
            ...item,
            price_pennies: Math.round(calculatePriceFromMargin(item.cost_price_pennies, newMargin)),
            markUp: newMargin,
          }
        }
        return item
      })
    }
    if (data && typeof data.discountPercent === "number") {
      batchItemChange(filteredItems, item => {
        if (quote.hasOpportunityCertificate && item.oppCertDisabled) {
          return item
        }
        if (item.cost_price_pennies && !item.price_locked) {
          return {
            ...item,
            discountPercent: data.discountPercent ?? null,
          }
        }
        return item
      })
    }
  }

  // sort items so that child items of a bom always appear under the parent
  function sortItemsWithBuilds(items: (IItem | IDeletedItem)[]) {
    const notDeletedItems = items.filter(x => !isDeleted(x)) as IItem[]
    const baseItems = notDeletedItems.filter(x => x.type !== itemTypes.buildSetItem)
    for (let i = 0; i < baseItems.length; i++) {
      const item = baseItems[i]
      if (!isDeleted(item) && item.type === itemTypes.buildSet) {
        const buildSetItems = notDeletedItems.filter(
          x =>
            !isDeleted(x) &&
            x.type === itemTypes.buildSetItem &&
            x.build_set_uid === item.build_set_uid,
        )
        baseItems.splice(i + 1, 0, ...buildSetItems)
      }
    }
    return baseItems
  }

  return (
    <React.Fragment>
      {kitBuilderModal}
      {buildSetBuilderModal}
      {dynamicModal}

      {settings.useExplicitAllocations && type === "order" && (
        <Fields.Checkbox
          value={autoAllocateMax}
          onChange={value => {
            setAutoAllocateMax(value)
          }}
          label="Auto allocate the maximum available stock to all items"
        />
      )}
      <div
        css={css`
          border-top: 1px solid #ddd;
          padding: 16px 0;
        `}
      >
        <Grid
          columns={[40, "auto", 40]}
          gap={8}
          alignItems="start"
          css={css`
            padding: 0 8px;
          `}
        >
          <div />
          <HeadingRow
            setAutoAllocateMax={setAutoAllocateMax}
            onOverride={handleItemDataOverride}
            handleItemChange={handleItemChange}
            useExplicitAllocations={settings.useExplicitAllocations}
            useStandardPricing={settings.useQuoteOrderStandardPricing}
            quote={quote}
            isCustomerViewing={isCustomerViewing}
            hideMarginColumn={hideMarginColumn}
            hidePriceColumn={hidePriceColumn}
          />
          <div />
        </Grid>
        {filteredItems.length === 0 ? (
          <React.Fragment>
            <Spacer size={6} />
            <div
              css={css`
                border: 2px dashed #ddd;
                padding: 32px;
              `}
            >
              <H3 align="center">No items... 📦</H3>
            </div>
          </React.Fragment>
        ) : (
          <React.Fragment>
            <DraggableList
              onOrderChange={handleItemOrderChange}
              items={sortItemsWithBuilds(filteredItems)}
              keyExtractor={item => item.id}
              renderItem={(item, DragHandle, isDragging) => (
                <RowContainer
                  isDragging={isDragging}
                  hasErrors={doesItemHaveErrors(item)}
                  color={item.type === itemTypes.buildSetItem ? brandColors.midGrey : "white"}
                >
                  <div>
                    <DragHandle>
                      <div
                        css={css`
                          background: rgba(255, 255, 255, 0.2);
                          transition: 0.3s;
                          padding: 12px;
                          text-align: center;
                          border-radius: 12px;
                          cursor: pointer;

                          &:hover {
                            background-color: #e5e5e5;
                          }
                        `}
                      >
                        <Icon icon={icons.grip} />
                      </div>
                    </DragHandle>
                  </div>

                  <Factory
                    key={item.id}
                    type={item.type ?? 0}
                    components={rowComponents}
                    props={{
                      data: item,
                      isCustomerViewing,
                      hideMarginColumn,
                      hidePriceColumn,
                      quote,
                      onChange: newItemPartial =>
                        handleItemChange(item.id, oldItem => ({
                          ...oldItem,
                          ...newItemPartial,
                        })),
                      rawOnChange: newItem => handleItemChange(item.id, newItem),
                      registerItem: options => registerItem(item, options),
                      errors: getErrorsForItem(item),
                      onRemove: () => handleRemoveItem(item),
                      onAppendItem: newItem =>
                        handleAddRow(itemTypes.sage, {
                          ...newItem,
                          parent_uid: genItemParentId("child", item.parent_uid),
                        }),
                      rawFunctions: {
                        onChangeSilent: newItemPartial =>
                          handleItemChangeSilent(item.id, oldItem => ({
                            ...oldItem,
                            ...newItemPartial,
                          })),
                        handleItemChange,
                        getErrorsForItem,
                        handleRemoveItem,
                        registerItem,
                        batchItemChange,
                        registeredItems,
                        loadProductDataForSageItemRow,
                        handleAddBOMItemRow: () => {
                          handleAddRow(itemTypes.buildSetItem, {
                            build_set_uid: item.build_set_uid,
                          })
                        },
                      },
                      type,
                      renderModal: renderDynamicModal,
                      displayAllocationColumn: !autoAllocateMax && settings.useExplicitAllocations,
                    }}
                  />
                </RowContainer>
              )}
            />
          </React.Fragment>
        )}
        <Spacer size={6} />
        <RowContainer color="#eee">
          <div />
          <TotalsRow quote={quote} isCustomerViewing={isCustomerViewing} />
          <div />
        </RowContainer>
        <Spacer />
        <Grid columns={["auto", { min: 300, max: 410 }]} gap={12}>
          <div>
            <ButtonsWrapper gap={4}>
              <Button
                onClick={() => handleAddRow(itemTypes.sage)}
                buttonColor={brandColors.lightNight}
              >
                <div
                  css={css`
                    display: flex;
                    align-items: center;
                    gap: 8px;
                  `}
                >
                  <span>
                    Add Sage Product
                  </span>
                  <span
                    css={css`
                      font-size: 0.75em;
                      opacity: 0.8;
                    `}
                  >
                    {modKey}+S
                  </span>
                </div>
              </Button>
              {type === "quote" && (
                <Button
                  onClick={() => handleAddRow(itemTypes.custom)}
                  buttonColor={brandColors.lightNight}
                >
                  <div
                    css={css`
                      display: flex;
                      align-items: center;
                      gap: 8px;
                    `}
                  >
                    <span>Add Custom Product</span>
                    <span
                      css={css`
                        font-size: 0.75em;
                        opacity: 0.8;
                      `}
                    >
                      {modKey}+D
                    </span>
                  </div>
                </Button>
              )}
              <Button
                onClick={() => handleAddRow(itemTypes.text)}
                buttonColor={brandColors.lightNight}
              >
                <div
                  css={css`
                    display: flex;
                    align-items: center;
                    gap: 8px;
                  `}
                >
                  <span>Add Text</span>
                  <span
                    css={css`
                      font-size: 0.75em;
                      opacity: 0.8;
                    `}
                  >
                    {modKey}+G
                  </span>
                </div>
              </Button>
              {settings.displayKits && (
                <Button
                  leftIcon={icons.kits}
                  onClick={() => handleAddKit()}
                  buttonColor={brandColors.mint}
                >
                  Quote master
                </Button>
              )}
              {betaFeature("bomItems") && (
                <Button
                  leftIcon={icons.bomItems}
                  onClick={() => handleAddBuildSet()}
                  buttonColor={brandColors.lightNight}
                  disabled={hideMarginColumn || hidePriceColumn}
                >
                  Add BOM Item
                </Button>
              )}
            </ButtonsWrapper>
          </div>
          <QuoteTotals
            quote={quote}
            onCarriagePriceChange={val => setQuote({ ...quote, carriage_pennies: val })}
            isCustomerViewing={isCustomerViewing}
          />
        </Grid>
      </div>
    </React.Fragment>
  )
}
